"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import {
  AlertCircle,
  ArrowRight,
  Award,
  BadgeCheck,
  CalendarCheck,
  CheckCircle2,
  ClipboardList,
  FileCheck2,
  FileText,
  ListChecks,
  Loader2,
  PlayCircle,
  RefreshCw,
  Scale,
  ShieldCheck,
  Users,
  type LucideIcon,
} from "lucide-react";
import HeaderPublic from "@components/layout/HeaderPublic";
import FooterPublic from "@components/layout/FooterPublic";
import SpeakerAvatars from "@components/ui/SpeakerAvatars";
import ClientLogosMarquee from "@components/sections/ClientLogosMarquee";
import { trackFormSubmit, trackGAEvent, trackError } from "@utils/ga4Tracking";
import pageData from "@data/landing-pages/ki-kurs-mittelstand.json";
import type { HeroVariantKey } from "../hero-variant";

const FREEMAIL_DOMAINS = [
  "gmail.com",
  "googlemail.com",
  "gmx.de",
  "gmx.net",
  "gmx.at",
  "gmx.ch",
  "web.de",
  "t-online.de",
  "outlook.com",
  "outlook.de",
  "hotmail.com",
  "hotmail.de",
  "live.de",
  "live.com",
  "yahoo.com",
  "yahoo.de",
  "icloud.com",
  "me.com",
  "aol.com",
  "freenet.de",
  "mail.de",
  "posteo.de",
] as const;

const ATTRIBUTION_KEYS = [
  "lp_source",
  "lp_ref_variant",
  "gclid",
  "wbraid",
  "gbraid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "matched_keyword",
  "ad_group",
  "campaign_id",
] as const;

const ATTRIBUTION_STORAGE_KEY = "dbi_lp_attribution";
const FIRST_TOUCH_KEY = "dbi_first_touch_date";
const SESSION_COUNT_KEY = "dbi_session_count";
const SESSION_FLAG_KEY = "dbi_session_counted";

const NACHWEIS_ICONS = [Award, FileText, Users, ListChecks, ShieldCheck, RefreshCw];

const OUTCOME_ICONS: Record<string, LucideIcon> = {
  PlayCircle,
  Scale,
  BadgeCheck,
};

const headingFont = { fontFamily: "var(--font-kanit, Kanit, sans-serif)" };

/* One header anatomy for every section: numbered eyebrow, identical H2 scale,
   lede capped at measure width — the vertical rhythm stays constant no matter
   what the section contains. */
function SectionHeader({
  eyebrow,
  title,
  lede,
  dark = false,
  align = "left",
}: {
  eyebrow: string;
  title: string;
  lede?: string;
  dark?: boolean;
  align?: "left" | "center";
}) {
  const centered = align === "center";
  return (
    <header className={`mb-10 md:mb-12 ${centered ? "text-center" : ""}`}>
      <p
        className={`text-xs font-semibold uppercase tracking-widest mb-3 ${
          dark ? "text-white/60" : "text-abis-primary/60"
        }`}
      >
        {eyebrow}
      </p>
      <h2
        className={`text-2xl md:text-3xl font-bold leading-tight ${
          dark ? "text-white" : "text-abis-primary"
        }`}
        style={headingFont}
      >
        {title}
      </h2>
      {lede && (
        <p
          className={`mt-4 leading-relaxed ${centered ? "mx-auto max-w-3xl" : "max-w-3xl"} ${
            dark ? "text-white/80" : "text-gray-600"
          }`}
        >
          {lede}
        </p>
      )}
    </header>
  );
}

type ScoreTier = "hot" | "mql" | "info" | "filter";

function scoreTier(score: number): ScoreTier {
  if (score >= 75) return "hot";
  if (score >= 45) return "mql";
  if (score >= 20) return "info";
  return "filter";
}

function isFreemail(email: string): boolean {
  const domain = email.trim().toLowerCase().split("@")[1];
  if (!domain) return false;
  return (FREEMAIL_DOMAINS as readonly string[]).includes(domain);
}

interface KursClientProps {
  variant: HeroVariantKey;
  nextCourseLabel: string;
  nextCourseTimeLabel: string;
}

export default function KursClient({
  variant,
  nextCourseLabel,
  nextCourseTimeLabel,
}: KursClientProps) {
  const hero = pageData.heroVariants[variant];
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [kursConsent, setKursConsent] = useState(false);
  const [zertifikatPaket, setZertifikatPaket] = useState(false);
  const [newsletterConsent, setNewsletterConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submittedTier, setSubmittedTier] = useState<ScoreTier | null>(null);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [attribution, setAttribution] = useState<Record<string, string>>({});

  const heroRef = useRef<HTMLElement>(null);
  const curriculumRef = useRef<HTMLElement>(null);
  const nachweisRef = useRef<HTMLElement>(null);
  const firedEvents = useRef<Set<string>>(new Set());

  const fireOnce = useCallback((eventName: string) => {
    if (firedEvents.current.has(eventName)) return;
    firedEvents.current.add(eventName);
    trackGAEvent(eventName, { page: "lp-ki-kurs-mittelstand", category: "lp_engagement" });
  }, []);

  // Attribution: merge URL params into sessionStorage, keep first touch and
  // session count in localStorage. All hidden fields from the Ads audit.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const stored: Record<string, string> = JSON.parse(
        sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY) ?? "{}"
      );
      ATTRIBUTION_KEYS.forEach((key) => {
        const value = params.get(key);
        if (value) stored[key] = value;
      });
      stored.lp_variant = variant;
      stored.landing_page_path = stored.landing_page_path ?? window.location.pathname;
      sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(stored));

      if (!localStorage.getItem(FIRST_TOUCH_KEY)) {
        localStorage.setItem(FIRST_TOUCH_KEY, new Date().toISOString());
      }
      if (!sessionStorage.getItem(SESSION_FLAG_KEY)) {
        sessionStorage.setItem(SESSION_FLAG_KEY, "1");
        const count = Number(localStorage.getItem(SESSION_COUNT_KEY) ?? "0") + 1;
        localStorage.setItem(SESSION_COUNT_KEY, String(count));
      }

      setAttribution({
        ...stored,
        first_touch_date: localStorage.getItem(FIRST_TOUCH_KEY) ?? "",
        session_count: localStorage.getItem(SESSION_COUNT_KEY) ?? "1",
      });
    } catch {
      // Storage unavailable (private mode) — attribution stays empty.
    }
  }, [variant]);

  // Sticky bar appears once the hero is scrolled past.
  useEffect(() => {
    const heroEl = heroRef.current;
    if (!heroEl) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyBar(!entry.isIntersecting && !submittedTier),
      { threshold: 0 }
    );
    observer.observe(heroEl);
    return () => observer.disconnect();
  }, [submittedTier]);

  // Micro-conversions: section views + 75 % scroll depth.
  useEffect(() => {
    const sections: Array<[React.RefObject<HTMLElement>, string]> = [
      [curriculumRef, "view_curriculum"],
      [nachweisRef, "view_nachweispaket"],
    ];
    const observers = sections
      .map(([ref, eventName]) => {
        if (!ref.current) return null;
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) fireOnce(eventName);
          },
          { threshold: 0.5 }
        );
        observer.observe(ref.current);
        return observer;
      })
      .filter(Boolean) as IntersectionObserver[];

    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable > 0 && window.scrollY / scrollable > 0.75) {
        fireOnce("scroll_75");
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      observers.forEach((observer) => observer.disconnect());
      window.removeEventListener("scroll", onScroll);
    };
  }, [fireOnce]);

  const formScore = useMemo(
    () =>
      pageData.form.fields.reduce((sum, field) => {
        const selected = field.options.find((option) => option.value === selections[field.id]);
        return sum + (selected?.score ?? 0);
      }, 0),
    [selections]
  );

  const scrollToForm = () => {
    document.getElementById("anmeldung")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleEmailBlur = () => {
    if (email && isFreemail(email)) {
      setEmailError(pageData.form.emailField.freemailError);
    } else {
      setEmailError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (isFreemail(email)) {
      setEmailError(pageData.form.emailField.freemailError);
      return;
    }
    if (!kursConsent) return;

    setIsSubmitting(true);
    setSubmitError("");

    const tier = scoreTier(formScore);
    // GA4/Ads conversion event per brief §9: hot / mql / info split.
    const conversionEvent =
      tier === "hot"
        ? "kurs_anmeldung_hot"
        : tier === "mql"
          ? "kurs_anmeldung_mql"
          : "kurs_anmeldung_info";

    try {
      const payload = {
        email: email.trim().toLowerCase(),
        ...selections,
        zertifikatPaket,
        newsletterConsent,
        gdprConsent: true,
        form_score: formScore,
        score_tier: tier,
        next_course_date: nextCourseLabel,
        ...attribution,
        timestamp: new Date().toISOString(),
        source: `KI-Basiskurs Anmeldung /services/ki-digitalisierung-mittelstand?v=${variant}`,
        userAgent: navigator.userAgent,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        formType: "ki_kurs_anmeldung",
        submissionId: `kurs_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      };

      const response = await fetch("/api/kurs-anmeldung", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      trackFormSubmit("ki_kurs_anmeldung", "kurs", true);
      trackGAEvent(conversionEvent, {
        page: "lp-ki-kurs-mittelstand",
        category: "lp_conversion",
        customParams: { form_score: formScore, lp_variant: variant },
      });
      setSubmittedTier(tier);
      setShowStickyBar(false);
    } catch (error) {
      console.error("❌ Kurs-Anmeldung fehlgeschlagen:", error);
      setSubmitError(
        "Die Anmeldung konnte nicht übertragen werden. Bitte versuchen Sie es erneut oder schreiben Sie an info@dbi-analytics.de."
      );
      trackFormSubmit("ki_kurs_anmeldung", "kurs", false);
      trackError(
        "kurs_anmeldung_submission",
        error instanceof Error ? error.message : "Unknown error",
        "lp-ki-kurs-mittelstand"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const thankYou = pageData.form.thankYou;
  const tierBlock =
    submittedTier === "hot"
      ? thankYou.hot
      : submittedTier === "mql"
        ? thankYou.mql
        : submittedTier === "info"
          ? thankYou.info
          : null;

  return (
    <div className="bg-white text-gray-900">
      <HeaderPublic />

      <main className="pt-28 md:pt-36">
        {/* SEKTION 1 — HERO */}
        <section ref={heroRef} className="max-w-6xl mx-auto px-4 pt-10 pb-12 md:pt-16 md:pb-16">
          <div className="grid md:grid-cols-[1fr_360px] gap-10 items-start">
            <div>
              <span className="inline-block rounded-full bg-abis-primary/5 border border-abis-primary/15 text-abis-primary text-xs font-semibold uppercase tracking-wide px-3 py-1 mb-5">
                {pageData.hero.badge}
              </span>
              <h1
                className="text-3xl md:text-[2.6rem] md:leading-[1.15] font-bold text-abis-primary mb-5"
                style={headingFont}
              >
                {hero.title}
              </h1>
              {hero.paragraphs.map((paragraph) => (
                <p key={paragraph} className="text-gray-700 text-lg leading-relaxed mb-4">
                  {paragraph}
                </p>
              ))}
              <div className="mt-6">
                <button
                  type="button"
                  onClick={scrollToForm}
                  className="inline-flex items-center gap-2 rounded-xl bg-abis-accent text-abis-primary font-bold px-7 py-4 text-lg shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all"
                >
                  {pageData.hero.primaryCta}
                  <ArrowRight className="w-5 h-5" aria-hidden="true" />
                </button>
                <p className="text-sm text-gray-500 mt-3">{pageData.hero.ctaMicrocopy}</p>
              </div>
            </div>

            {/* Terminkarte */}
            <aside className="rounded-2xl border border-abis-primary/15 bg-gradient-to-br from-abis-primary to-abis-primary/90 text-white p-6 shadow-xl md:sticky md:top-6">
              <div className="flex items-center gap-3 mb-4">
                <SpeakerAvatars
                  speakers={pageData.speakers.items}
                  size={44}
                />
                <p className="text-xs text-white/80 leading-snug">
                  {pageData.speakers.items.map((speaker) => speaker.name).join(" & ")}
                </p>
              </div>
              <p className="text-xs font-semibold uppercase tracking-widest text-white/70 mb-1">
                {pageData.hero.termCard.eyebrow}
              </p>
              <p className="text-2xl font-bold text-white mb-1" style={headingFont}>
                {nextCourseLabel}
              </p>
              <p className="text-white/80 text-sm mb-1">
                {nextCourseTimeLabel} · live online · {pageData.hero.termCard.seatsLabel}
              </p>
              <p className="text-abis-accent text-sm font-semibold mb-5">
                {pageData.hero.termCard.priceLabel}
              </p>
              <ul className="space-y-3">
                {pageData.hero.termCard.items.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-white/90">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-abis-accent" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={scrollToForm}
                className="mt-6 w-full rounded-lg bg-abis-accent text-abis-primary font-bold py-3 hover:bg-abis-accent/90 transition-colors"
              >
                {pageData.stickyBar.ctaLabel}
              </button>
            </aside>
          </div>

          <div className="mt-12 md:mt-14">
            <p className="text-xs font-semibold uppercase tracking-widest text-abis-primary/60 mb-3">
              {pageData.hero.outcomes.eyebrow}
            </p>
            <h2
              className="text-2xl md:text-3xl font-bold text-abis-primary mb-6"
              style={headingFont}
            >
              {pageData.hero.outcomes.title}
            </h2>
            <ul className="grid sm:grid-cols-3 gap-4 list-none p-0 m-0">
              {pageData.hero.outcomes.items.map((item) => {
                const Icon = OUTCOME_ICONS[item.iconName];
                if (!Icon) {
                  throw new Error(`Missing outcome icon: ${item.iconName}`);
                }
                return (
                  <li
                    key={item.title}
                    className="rounded-2xl border border-abis-accent/30 bg-gradient-to-b from-abis-accent/[0.12] to-white p-5 md:p-6"
                  >
                    <span className="inline-flex w-10 h-10 items-center justify-center rounded-xl bg-abis-primary text-white mb-4">
                      <Icon className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <h3 className="font-bold text-abis-primary text-lg mb-2" style={headingFont}>
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.text}</p>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Trust-Leiste */}
          <ul className="mt-10 flex flex-wrap gap-x-6 gap-y-2 border-t border-gray-100 pt-6">
            {pageData.hero.trustItems.map((item) => (
              <li key={item} className="flex items-center gap-2 text-sm text-gray-600">
                <ShieldCheck className="w-4 h-4 text-abis-primary/60" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>

          {/* AEO-Definitionsblock */}
          <p className="mt-6 rounded-xl bg-gray-50 border border-gray-200 px-5 py-4 text-sm text-gray-600 leading-relaxed">
            <em>{pageData.hero.aeoDefinition}</em>
          </p>

          {/* Bildband — ungeschnitten in voller Höhe, Caption liegt im Bild */}
          <figure className="mt-10 relative max-w-4xl mx-auto rounded-2xl overflow-hidden">
            <Image
              src={pageData.hero.image.src}
              alt={pageData.hero.image.alt}
              width={1024}
              height={1024}
              sizes="(max-width: 896px) 100vw, 896px"
              className="w-full h-auto"
            />
            <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent px-6 pt-16 pb-5 text-white text-sm md:text-base font-medium">
              {pageData.hero.image.caption}
            </figcaption>
          </figure>
        </section>

        {/* DER WEG — Timeline in vier Etappen. Steht direkt hinter dem Hero:
            Transformations-Sucher sollen als Erstes den Weg sehen, nicht das
            Problem. */}
        <section id="weg" className="max-w-6xl mx-auto px-4 py-16 md:py-20">
          <SectionHeader
            eyebrow={`01 · ${pageData.weg.eyebrow}`}
            title={pageData.weg.title}
            lede={pageData.weg.lede}
          />
          <ol className="relative border-l-2 border-abis-primary/15 ml-4 md:ml-0 md:border-l-0 md:grid md:grid-cols-4 md:gap-6 list-none p-0">
            {pageData.weg.steps.map((step, index) => (
              <li key={step.phase} className="relative pl-8 pb-10 md:pl-0 md:pb-0">
                {/* Mobile: Punkt auf der Linie · Desktop: nummerierte Karte */}
                <span
                  className="absolute -left-[11px] top-0 w-5 h-5 rounded-full bg-abis-accent border-4 border-white shadow md:hidden"
                  aria-hidden="true"
                />
                <div className="md:rounded-xl md:border md:border-gray-200 md:p-6 md:h-full md:hover:border-abis-primary/30 md:transition-colors">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="hidden md:flex w-8 h-8 rounded-full bg-abis-primary text-white text-sm font-bold items-center justify-center">
                      {index + 1}
                    </span>
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                        {step.phase}
                      </p>
                      <p className="text-xs text-abis-primary/70 font-semibold">{step.duration}</p>
                    </div>
                  </div>
                  <h3 className="font-bold text-abis-primary text-lg mb-2" style={headingFont}>
                    {step.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* DAS PROBLEM — drei Sätze aus dem Mittelstand */}
        <section className="bg-abis-primary/[0.04] border-y border-abis-primary/10">
          <div className="max-w-6xl mx-auto px-4 py-16 md:py-20">
            <SectionHeader eyebrow="02 · Stimmen aus dem Mittelstand" title={pageData.problem.title} />
            <div className="grid md:grid-cols-3 gap-6">
              {pageData.problem.items.map((item) => (
                <div key={item.quote} className="rounded-xl bg-white border border-gray-200 p-6">
                  <p className="font-semibold text-abis-primary mb-2">{item.quote}</p>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.answer}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              {pageData.problem.chips.map((chip) => (
                <span
                  key={chip}
                  className="rounded-full bg-white border border-abis-primary/20 text-abis-primary text-xs font-semibold px-4 py-2"
                >
                  {chip}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* STORY — DBIs eigener Weg ins KI-Zeitalter */}
        <section id="story" className="bg-gray-50 border-y border-gray-100">
          <div className="max-w-6xl mx-auto px-4 py-16 md:py-20">
            <SectionHeader
              eyebrow={`03 · ${pageData.story.eyebrow}`}
              title={pageData.story.title}
              lede={pageData.story.lede}
            />
            <div className="grid md:grid-cols-[1fr_400px] gap-10 items-start">
              <ol className="relative border-l-2 border-abis-primary/15 ml-3 list-none p-0">
                {pageData.story.milestones.map((milestone) => (
                  <li key={milestone.year} className="relative pl-8 pb-8 last:pb-0">
                    <span
                      className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-abis-primary border-4 border-gray-50"
                      aria-hidden="true"
                    />
                    <p className="text-sm font-bold text-abis-accent mb-0.5">{milestone.year}</p>
                    <h3 className="font-bold text-abis-primary mb-1.5" style={headingFont}>
                      {milestone.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{milestone.text}</p>
                  </li>
                ))}
              </ol>
              <div className="md:sticky md:top-28 space-y-6">
                <div className="relative h-64 md:h-80 rounded-2xl overflow-hidden shadow-lg">
                  <Image
                    src={pageData.story.image}
                    alt={pageData.story.imageAlt}
                    fill
                    sizes="(max-width: 768px) 100vw, 400px"
                    className="object-cover"
                  />
                </div>
                {/* Expertenzitat */}
                <blockquote className="rounded-2xl bg-white border border-gray-200 p-6">
                  <p className="text-gray-700 leading-relaxed italic mb-4">
                    „{pageData.expertQuote.quote}“
                  </p>
                  <footer className="flex items-center gap-3">
                    <span className="relative w-16 h-16 rounded-full overflow-hidden shrink-0 ring-2 ring-abis-primary/10">
                      <Image
                        src={pageData.expertQuote.image}
                        alt=""
                        fill
                        sizes="64px"
                        className="object-cover object-top"
                      />
                    </span>
                    <span>
                      <span className="block font-bold text-abis-primary text-sm">
                        {pageData.expertQuote.author}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {pageData.expertQuote.role}
                      </span>
                    </span>
                  </footer>
                </blockquote>
              </div>
            </div>

            {/* Referenzkunden — direkt hinter der Unternehmens-Timeline: erst
                die eigene Geschichte, dann die Unternehmen, die sie belegen. */}
            <div className="mt-14">
              <p className="text-xs font-semibold uppercase tracking-widest text-abis-primary/60 mb-2 text-center">
                {pageData.clientsTrust.eyebrow}
              </p>
              <p className="text-center font-semibold text-abis-primary mb-2" style={headingFont}>
                {pageData.clientsTrust.title}
              </p>
              <p className="text-center text-sm text-gray-600 mb-6 max-w-2xl mx-auto">
                {pageData.clientsTrust.lede}
              </p>
              <ClientLogosMarquee />
            </div>

            {/* Zahlen, Daten, Fakten aus dem eigenen Umstieg */}
            <div className="mt-14">
              <h3 className="text-xl md:text-2xl font-bold text-abis-primary mb-2" style={headingFont}>
                {pageData.story.stats.title}
              </h3>
              <p className="text-gray-600 mb-8">{pageData.story.stats.lede}</p>
              <div className="grid sm:grid-cols-3 gap-5">
                {pageData.story.stats.items.map((stat) => (
                  <div key={stat.label} className="rounded-xl bg-white border border-gray-200 p-6">
                    <p className="text-4xl font-bold text-abis-primary mb-1" style={headingFont}>
                      {stat.value}
                    </p>
                    <p className="text-sm font-semibold text-abis-primary/80 mb-3">{stat.label}</p>
                    <p className="text-gray-600 text-sm leading-relaxed">{stat.text}</p>
                  </div>
                ))}
              </div>
              <div className="mt-5 rounded-xl bg-abis-primary text-white p-6 flex flex-col md:flex-row md:items-center gap-4">
                <ShieldCheck className="w-10 h-10 text-abis-accent shrink-0" aria-hidden="true" />
                <div>
                  <p className="font-bold text-white mb-1" style={headingFont}>
                    {pageData.story.stats.governance.label}
                  </p>
                  <p className="text-white/80 text-sm leading-relaxed">
                    {pageData.story.stats.governance.text}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* IHRE TRAINER */}
        <section id="trainer" className="max-w-6xl mx-auto px-4 py-16 md:py-20">
          <SectionHeader
            eyebrow={`04 · ${pageData.speakers.eyebrow}`}
            title={pageData.speakers.title}
            lede={pageData.speakers.lede}
            align="center"
          />
          <div className="grid md:grid-cols-2 gap-6">
            {pageData.speakers.items.map((speaker) => (
              <article
                key={speaker.name}
                className="rounded-2xl border border-gray-200 bg-white p-6 md:p-8 flex flex-col items-center text-center gap-5"
              >
                <div className="relative w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden shrink-0 mx-auto ring-4 ring-abis-primary/10">
                  <Image
                    src={speaker.image}
                    alt={speaker.imageAlt}
                    fill
                    sizes="128px"
                    className="object-cover"
                  />
                </div>
                <div className="min-w-0 w-full text-center">
                  <h3 className="text-xl font-bold text-abis-primary" style={headingFont}>
                    {speaker.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-3">{speaker.role}</p>
                  <p className="text-gray-700 text-sm leading-relaxed mb-4">{speaker.bio}</p>
                  <ul className="space-y-2 inline-flex flex-col items-start text-left mx-auto">
                    {speaker.highlights.map((highlight) => (
                      <li key={highlight} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-abis-accent" aria-hidden="true" />
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                  {"profileHref" in speaker &&
                    "profileLabel" in speaker &&
                    speaker.profileHref &&
                    speaker.profileLabel && (
                    <Link
                      href={speaker.profileHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-1.5 mt-4 text-sm font-semibold text-abis-primary hover:text-abis-accent transition-colors"
                    >
                      {speaker.profileLabel}
                      <ArrowRight className="w-4 h-4" aria-hidden="true" />
                    </Link>
                  )}
                </div>
              </article>
            ))}
          </div>

        </section>

        {/* SEKTION 3 — DAS PROGRAMM */}
        <section ref={curriculumRef} id="curriculum" className="max-w-6xl mx-auto px-4 py-16 md:py-20">
          <SectionHeader eyebrow="05 · Programm" title={pageData.curriculum.title} />
          <div className="grid sm:grid-cols-2 gap-6">
            {pageData.curriculum.modules.map((module) => (
              <div key={module.number} className="rounded-xl border border-gray-200 p-6 hover:border-abis-primary/30 transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <span className="w-9 h-9 rounded-full bg-abis-primary text-white font-bold flex items-center justify-center">
                    {module.number}
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                    {module.duration}
                  </span>
                </div>
                <h3 className="font-bold text-abis-primary text-lg mb-2" style={headingFont}>
                  {module.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">{module.description}</p>
                {"image" in module && module.image && (
                  <div className="relative mt-4 h-40 rounded-lg overflow-hidden border border-gray-100">
                    <Image
                      src={module.image}
                      alt={module.imageAlt}
                      fill
                      sizes="(max-width: 640px) 100vw, 550px"
                      className="object-cover object-top"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
          {/* Follow-up: zwei Check-ins als Lerngruppe */}
          <div className="mt-8 rounded-2xl border-2 border-abis-accent/40 bg-abis-accent/[0.06] p-6 md:p-8">
            <span className="inline-block rounded-full bg-abis-accent text-abis-primary text-xs font-bold uppercase tracking-wide px-3 py-1 mb-3">
              {pageData.curriculum.followUp.badge}
            </span>
            <h3 className="text-lg md:text-xl font-bold text-abis-primary mb-3" style={headingFont}>
              {pageData.curriculum.followUp.title}
            </h3>
            <p className="text-gray-700 leading-relaxed max-w-3xl">
              {pageData.curriculum.followUp.text}
            </p>
          </div>
          <p className="mt-6 text-sm text-gray-500">{pageData.curriculum.note}</p>
        </section>

        {/* SEKTION 4 — DAS NACHWEISPAKET */}
        <section ref={nachweisRef} id="nachweispaket" className="bg-abis-primary text-white">
          <div className="max-w-6xl mx-auto px-4 py-16 md:py-20">
            <SectionHeader
              eyebrow="06 · Nachweispaket"
              title={pageData.nachweispaket.title}
              lede={pageData.nachweispaket.lede}
              dark
            />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {pageData.nachweispaket.items.map((item, index) => {
                const Icon = NACHWEIS_ICONS[index] ?? FileCheck2;
                return (
                  <div key={item.title} className="rounded-xl bg-white/[0.07] border border-white/15 p-5">
                    <Icon className="w-6 h-6 text-abis-accent mb-3" aria-hidden="true" />
                    <h3 className="font-bold text-white mb-1.5">{item.title}</h3>
                    <p className="text-white/75 text-sm leading-relaxed">{item.description}</p>
                  </div>
                );
              })}
            </div>
            <p className="mt-8 text-white/80 text-sm max-w-3xl">{pageData.nachweispaket.ownership}</p>
            <p className="mt-4 text-white/60 text-xs max-w-3xl italic">
              {pageData.nachweispaket.legalNote}
            </p>
          </div>
        </section>

        {/* SEKTION 5 — FÜR WEN */}
        <section className="max-w-6xl mx-auto px-4 py-16 md:py-20">
          <SectionHeader eyebrow="07 · Zielgruppe" title={pageData.fuerWen.title} />
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-xl border border-green-200 bg-green-50/60 p-6">
              <h3 className="text-lg font-bold text-green-800 mb-2" style={headingFont}>{pageData.fuerWen.fit.heading}</h3>
              <p className="text-gray-700 text-sm leading-relaxed">{pageData.fuerWen.fit.text}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
              <h3 className="text-lg font-bold text-gray-700 mb-2" style={headingFont}>{pageData.fuerWen.noFit.heading}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{pageData.fuerWen.noFit.text}</p>
            </div>
          </div>
        </section>

        {/* SEKTION 6 — WARUM KOSTENFREI */}
        <section className="bg-gray-50 border-y border-gray-100">
          <div className="max-w-3xl mx-auto px-4 py-16 md:py-20">
            <SectionHeader eyebrow="08 · Preis" title={pageData.warumKostenfrei.title} />
            {pageData.warumKostenfrei.paragraphs.map((paragraph) => (
              <p key={paragraph} className="text-gray-700 leading-relaxed mb-4">
                {paragraph}
              </p>
            ))}
          </div>
        </section>

        {/* SEKTION 7 — ANMELDUNG */}
        <section id="anmeldung" className="max-w-3xl mx-auto px-4 py-16 md:py-20 scroll-mt-8">
          {submittedTier ? (
            <div className="rounded-2xl border border-green-200 bg-green-50/60 p-8 text-center">
              <CalendarCheck className="w-14 h-14 text-green-600 mx-auto mb-4" aria-hidden="true" />
              <h2 className="text-2xl font-bold text-abis-primary mb-3" style={headingFont}>
                {thankYou.common.title}
              </h2>
              <p className="text-gray-700 leading-relaxed max-w-xl mx-auto">{thankYou.common.text}</p>
              {tierBlock && (
                <div className="mt-8 rounded-xl bg-white border border-gray-200 p-6 text-left">
                  <h3 className="font-bold text-abis-primary mb-2">{tierBlock.heading}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">{tierBlock.text}</p>
                  <Link
                    href={tierBlock.ctaHref}
                    className="inline-flex items-center gap-2 rounded-lg bg-abis-primary text-white font-semibold px-5 py-3 text-sm hover:bg-abis-primary/90 transition-colors"
                  >
                    {tierBlock.ctaLabel}
                    <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-2xl border border-gray-200 bg-white p-6 md:p-10 shadow-sm">
              <SectionHeader
                eyebrow="09 · Anmeldung"
                title={`${pageData.form.title} — nächster Termin ${nextCourseLabel}`}
                lede={pageData.form.subtitle}
              />

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="kurs-email" className="block text-sm font-semibold text-gray-700 mb-1.5">
                    {pageData.form.emailField.label} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="kurs-email"
                    name="email"
                    required
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailError) setEmailError("");
                    }}
                    onBlur={handleEmailBlur}
                    disabled={isSubmitting}
                    placeholder={pageData.form.emailField.placeholder}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-abis-primary focus:border-transparent disabled:opacity-50 transition-all"
                  />
                  {emailError && (
                    <p className="mt-2 flex items-start gap-2 text-sm text-red-600">
                      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                      {emailError}
                    </p>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  {pageData.form.fields.map((field) => (
                    <div key={field.id}>
                      <label
                        htmlFor={`kurs-${field.id}`}
                        className="block text-sm font-semibold text-gray-700 mb-1.5"
                      >
                        {field.label} <span className="text-red-500">*</span>
                      </label>
                      <select
                        id={`kurs-${field.id}`}
                        name={field.id}
                        required
                        value={selections[field.id] ?? ""}
                        onChange={(e) =>
                          setSelections((prev) => ({ ...prev, [field.id]: e.target.value }))
                        }
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-abis-primary focus:border-transparent disabled:opacity-50 transition-all"
                      >
                        <option value="" disabled>
                          Bitte wählen …
                        </option>
                        {field.options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>

                {/* Double-Opt-In sauber getrennt: Kursanmeldung ≠ Zertifikatspaket ≠ Newsletter. */}
                <div className="space-y-3 pt-2">
                  <label className="flex items-start gap-3 cursor-pointer text-sm text-gray-600 rounded-lg border border-abis-primary/15 bg-abis-primary/[0.03] p-3">
                    <input
                      type="checkbox"
                      checked={zertifikatPaket}
                      onChange={(e) => setZertifikatPaket(e.target.checked)}
                      className="mt-0.5 w-4 h-4 min-w-0 min-h-0 accent-abis-primary shrink-0 cursor-pointer"
                    />
                    <span>{pageData.form.consent.zertifikat}</span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={kursConsent}
                      onChange={(e) => setKursConsent(e.target.checked)}
                      required
                      className="mt-0.5 w-4 h-4 min-w-0 min-h-0 accent-abis-primary shrink-0 cursor-pointer"
                    />
                    <span>
                      {pageData.form.consent.kurs}{" "}
                      <Link
                        href="/imprint#datenschutz"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-abis-primary underline"
                      >
                        Datenschutzerklärung
                      </Link>
                      <span className="text-red-500"> *</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={newsletterConsent}
                      onChange={(e) => setNewsletterConsent(e.target.checked)}
                      className="mt-0.5 w-4 h-4 min-w-0 min-h-0 accent-abis-primary shrink-0 cursor-pointer"
                    />
                    <span>{pageData.form.consent.newsletter}</span>
                  </label>
                </div>

                {submitError && (
                  <p className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                    {submitError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || !kursConsent}
                  className="w-full rounded-xl bg-abis-accent text-abis-primary font-bold py-4 text-lg shadow-lg hover:shadow-xl hover:scale-[1.01] active:scale-[0.99] transition-all disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                      Wird gesendet …
                    </>
                  ) : (
                    <>
                      <ClipboardList className="w-5 h-5" aria-hidden="true" />
                      {pageData.form.submitLabel}
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-500 text-center">{pageData.form.afterSubmitNote}</p>
              </form>
            </div>
          )}
        </section>

        <section id="whitepaper" className="bg-gray-50 border-y border-gray-100">
          <div className="max-w-3xl mx-auto px-4 py-16 md:py-20">
            <SectionHeader
              eyebrow={`10 · ${pageData.whitepaper.eyebrow}`}
              title={pageData.whitepaper.question}
            />

            <article
              id="whitepaper-answer"
              className="relative rounded-2xl border border-gray-200 bg-white p-6 md:p-8 pl-7 md:pl-9"
            >
              <span
                className="absolute left-0 top-8 w-1 h-12 rounded-r bg-abis-accent"
                aria-hidden="true"
              />
              <span className="inline-block rounded-full bg-abis-accent/15 text-abis-primary text-xs font-bold uppercase tracking-wide px-3 py-1 mb-4">
                {pageData.whitepaper.badge}
              </span>
              {pageData.whitepaper.answer.map((paragraph) => (
                <p key={paragraph} className="text-gray-700 leading-relaxed mb-4 last:mb-0 text-base md:text-lg">
                  {paragraph}
                </p>
              ))}
              <p className="mt-6 rounded-xl bg-abis-primary/[0.04] border border-abis-primary/10 px-5 py-4 text-sm md:text-base text-gray-800 leading-relaxed">
                <span className="block text-xs font-bold uppercase tracking-widest text-abis-primary/70 mb-1">
                  {pageData.whitepaper.takeaway.label}
                </span>
                {pageData.whitepaper.takeaway.text}
              </p>
            </article>
          </div>
        </section>

        {/* FAQ (Antworten stehen als <details> vollständig im HTML) */}
        <section id="faq" className="max-w-3xl mx-auto px-4 py-16 md:py-20">
          <SectionHeader eyebrow="11 · FAQ" title={pageData.faq.title} />
          <div className="divide-y divide-gray-200 border-y border-gray-200">
            {pageData.faq.faqs.map((item) => (
              <details
                key={item.id}
                className="group py-4"
                onToggle={(e) => {
                  if (item.id === "preis" && (e.target as HTMLDetailsElement).open) {
                    fireOnce("faq_preis");
                  }
                }}
              >
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-semibold text-abis-primary">
                  {item.question}
                  <span className="text-gray-400 group-open:rotate-45 transition-transform text-xl leading-none" aria-hidden="true">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-gray-600 text-sm leading-relaxed">{item.answer}</p>
              </details>
            ))}
          </div>
        </section>
      </main>

      <FooterPublic />

      {/* STICKY-BAR ab Ende Hero */}
      {showStickyBar && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-abis-primary text-white shadow-[0_-4px_20px_rgba(0,0,0,0.15)]">
          <div className="max-w-6xl mx-auto px-4 min-h-[64px] flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <SpeakerAvatars speakers={pageData.speakers.items} size={36} className="hidden sm:flex" />
              <p className="m-0 text-sm leading-none text-white flex items-center">
                <span className="font-semibold">Nächster Termin {nextCourseLabel}</span>
                <span className="hidden sm:inline text-white/70"> · {nextCourseTimeLabel} · max. 25 Plätze</span>
              </p>
            </div>
            <button
              type="button"
              onClick={scrollToForm}
              className="shrink-0 rounded-lg bg-abis-accent text-abis-primary font-bold px-5 py-2.5 text-sm hover:bg-abis-accent/90 transition-colors"
            >
              {pageData.stickyBar.ctaLabel} →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
